// admin/src/lib/useAuth.tsx
//
// Auth state for the whole app: Supabase session + the is_admin() check
// (wave 10 spec §3 Lane D). No sign-up here -- accounts come from the
// Calyard app. A signed-in account that fails is_admin() sees the
// not-an-admin page, so nothing admin-only ever renders for it.
//
// Staying signed in (keepSignedIn.ts): every new refresh token is
// mirrored into the Worker's HttpOnly cookie, and an empty localStorage
// on startup is refilled from it. Only "Sign out" forgets it.
//
// Nothing here awaits a Supabase call inside onAuthStateChange: the
// callback runs while supabase-js holds its auth lock, so awaiting
// another auth-aware call there can hang (supabase-js docs). Follow-up
// work is deferred with setTimeout instead.
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { forgetRefreshToken, keepRefreshToken, restoreRefreshToken } from './keepSignedIn';

type AuthState = {
  loading: boolean;
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  /** Set once `is_admin()` has resolved (true or false) for the current session. */
  adminChecked: boolean;
  signOut: () => Promise<void>;
  recheckAdmin: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

const ADMIN_CHECK_ATTEMPTS = 3;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  // The user id the admin state belongs to; a token refresh keeps it.
  const checkedFor = useRef<string | null>(null);

  async function checkAdmin(userId: string) {
    for (let attempt = 1; attempt <= ADMIN_CHECK_ATTEMPTS; attempt++) {
      const { data, error } = await supabase.rpc('is_admin');
      if (checkedFor.current !== userId) return;
      if (!error) {
        setIsAdmin(Boolean(data));
        setAdminChecked(true);
        return;
      }
      // A network blip must not read as "not an admin": retry first.
      if (attempt < ADMIN_CHECK_ATTEMPTS) await new Promise((r) => setTimeout(r, 600 * attempt));
    }
    setIsAdmin(false);
    setAdminChecked(true);
  }

  useEffect(() => {
    let active = true;

    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      if (newSession?.refresh_token && event !== 'SIGNED_OUT') {
        const token = newSession.refresh_token;
        setTimeout(() => void keepRefreshToken(token), 0);
      }
      const userId = newSession?.user.id ?? null;
      if (userId !== checkedFor.current) {
        checkedFor.current = userId;
        setIsAdmin(false);
        setAdminChecked(false);
        if (userId) setTimeout(() => void checkAdmin(userId), 0);
      }
    });

    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        const kept = await restoreRefreshToken();
        if (kept) {
          const { error } = await supabase.auth.refreshSession({ refresh_token: kept });
          if (error) await forgetRefreshToken();
        }
      }
      if (active) setLoading(false);
    })();

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      loading,
      session,
      user: session?.user ?? null,
      isAdmin,
      adminChecked,
      signOut: async () => {
        await forgetRefreshToken();
        // 'local': end this browser's session only, not the account's
        // sessions in the Calyard app.
        await supabase.auth.signOut({ scope: 'local' });
      },
      recheckAdmin: async () => {
        if (checkedFor.current) await checkAdmin(checkedFor.current);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, session, isAdmin, adminChecked],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
